/**
 * Authenticated Fetch Utility
 *
 * Automatically includes Supabase authentication tokens in API requests
 * Solves "401 Authentication required" errors on save operations
 */

import { supabase } from './supabase';
import { API_CONFIG } from '@/config/environment';

export interface AuthenticatedFetchOptions extends RequestInit {
  skipAuth?: boolean; // Skip auth header for public endpoints
}

/**
 * Fetch wrapper that automatically includes Supabase Bearer token
 * Use this instead of regular fetch() for authenticated API calls
 */
export async function authenticatedFetch(
  urlOrPath: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { skipAuth = false, headers = {}, ...restOptions } = options;

  // Build full URL if path is provided
  const url = urlOrPath.startsWith('http')
    ? urlOrPath
    : API_CONFIG.getApiUrl(urlOrPath);

  // Get current Supabase session
  const { data: { session } } = await supabase.auth.getSession();

  // Prepare headers
  const authHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...headers,
  };

  // Add Bearer token if session exists and auth not skipped
  if (!skipAuth && session?.access_token) {
    authHeaders['Authorization'] = `Bearer ${session.access_token}`;
    console.log('[AUTH-FETCH] Including Bearer token for:', url);
  } else if (!skipAuth) {
    console.warn('[AUTH-FETCH] No session found, request may fail:', url);
  }

  // Make request with authentication
  return fetch(url, {
    ...restOptions,
    credentials: 'include', // Also include cookies for backwards compatibility
    headers: authHeaders,
  });
}

/**
 * Convenience method for GET requests
 */
export async function authenticatedGet(
  urlOrPath: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  return authenticatedFetch(urlOrPath, {
    ...options,
    method: 'GET',
  });
}

/**
 * Convenience method for POST requests
 */
export async function authenticatedPost(
  urlOrPath: string,
  body?: any,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  return authenticatedFetch(urlOrPath, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Convenience method for PUT requests
 */
export async function authenticatedPut(
  urlOrPath: string,
  body?: any,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  return authenticatedFetch(urlOrPath, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Convenience method for DELETE requests
 */
export async function authenticatedDelete(
  urlOrPath: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  return authenticatedFetch(urlOrPath, {
    ...options,
    method: 'DELETE',
  });
}

/**
 * Helper to handle common API response patterns
 */
export async function handleApiResponse<T = any>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `API error: ${response.status}`;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorJson.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }

  return response.text() as any;
}
